import React, { useState } from 'react';
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import { GenerateWorkshopEnrollmentNumber } from '../../components/GenerateThink5whysCode';

const TrainingEnrollmentForm = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();

  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };
  
  const [autoEnrollmentNumber] = useState(GenerateWorkshopEnrollmentNumber());
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    enrollmentNumber: autoEnrollmentNumber,
    isTraining: false,
    trainingWorkshopId: null,
    title: '',
    enrollmentRemarks: '',
    userId: authState.user._id,
    createdBy: authState.user._id,
    dateCreated: new Date(),
    amount: 0.0,
    isPaid: false,
    paymentMode: null,
    paymentDate: null,
    paymentRemarks: '',
    isActive: true,
    isDeleted: false,
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = (data) => {
    const errors = {};
    if (!data.enrollmentRemarks) {
      errors.enrollmentRemarks = 'Please input remarks';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm(formData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) return;

    const config = {
      url: '/trainingworkshop/enroll',
      method: 'post',
      data: { formData, userId: authState.user._id },
      headers: { Authorization: authState.token },
    };

    try {
      await fetchData(config);
      navigate('/training-workshops');
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const fieldError = (field) => (
    <p className={`mt-1 text-pink-500 text-sm ${formErrors[field] ? 'block' : 'hidden'}`}>
      <i className="mr-2 fa-solid fa-circle-exclamation"></i>
      {formErrors[field]}
    </p>
  );

  return (
    <div className='p-2'>
      <header className="sticky top-0 z-50 bg-black border-b border-green-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-xl font-bold text-yellow-300">Training / Workshop Enrollment Form</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-cyan-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="p-2 w-8 rounded-md bg-red-600 hover:bg-red-700 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-3 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
              >
                Login
              </button>
            </>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-xl text-cyan-300"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* Mobile menu content */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-cyan-300 text-xs lg:text-sm">{userName}</div>
              <div className="flex gap-3">
                <button
                  onClick={handleLogout}
                  className="p-2 mt-4 rounded-md w-8 bg-red-600 hover:bg-red-700"
                >
                  ⏻
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6">
              <Link to="/login" className="text-cyan-300">Login</Link>
              {/* <Link to="/signup" className="text-cyan-300">Register</Link> */}
            </div>
          )}

          <div className="mt-3 flex flex-wrap justify-center gap-3 text-sm text-gray-200">
            <a href="/sheetal-about" className="hover:text-yellow-300">💭 About SHEETAL</a>
            <a href="/sheetal-faq" className="hover:text-yellow-200">🤔 FAQ</a>
          </div>
        </div>
      )}

    <div className="bg-black text-gray-100 shadow-md rounded-lg p-4 sm:p-6 mx-auto w-full">
      {loading ? (
        <Loader />
      ) : (
        <form className="space-y-2">
          <div>
            <p className="text-white font-medium">
              Enrollment Number: <span className="text-white">{autoEnrollmentNumber}</span>
            </p>
            <div className="flex flex-col w-full">
              <label htmlFor="title" className="text-white font-medium mb-1">
                Training Title / Code:
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter training title or code..."
                style={{width: "100%"}}
                className="w-full p-2 rounded-md border border-gray-700 bg-gray-800 text-gray-100 
                          focus:outline-none focus:ring-2 focus:ring-yellow-200 focus:border-yellow-200 
                          text-sm sm:text-base transition-all duration-200"
              />
            </div>
          </div>

          <div className="flex flex-col w-full">
            <label htmlFor="enrollmentRemarks" className="mb-1 font-medium">
              Remarks:
            </label>
            <textarea
                name="enrollmentRemarks"
                id="enrollmentRemarks"
                value={formData.enrollmentRemarks}
                placeholder="Input remarks..."
                onChange={handleChange}
                maxLength="500"
                style={{width: "100%", height:"300px", resize: 'vertical'}}
                className="w-full resize-y overflow-auto p-2 rounded-md border border-gray-700 bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 min-h-[80px] max-h-96"
              ></textarea>
            {fieldError('enrollmentRemarks')}
          </div>

          {Object.keys(formErrors).length > 0 && (
            <div className="text-red-400 text-sm">
              {Object.keys(formErrors).map((key) => (
                <p key={key}>{formErrors[key]}</p>
              ))}
            </div>
          )}

          <div className="flex flex-row sm:flex-row justify-center gap-4">
            <button
              type="button"
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-md font-medium transition-colors"
              onClick={handleSubmit}
            >
              Submit
            </button>
            <button
              type="button"
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
              onClick={() => navigate('/training-workshops')}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
    <footer className="bg-gray-900 border-t border-green-400 text-gray-400 py-4">
      <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</div>
        <div className="flex flex-wrap gap-4 text-sm">
          <Link to="/about-us" className="hover:text-cyan-300">About</Link>
          <Link to="/privacy-policy" className="hover:text-cyan-300">Privacy Policy</Link>
          <Link to="/terms-of-use" className="hover:text-cyan-300">Terms of Use</Link>
          <Link to="/contact-us" className="hover:text-cyan-300">Contact</Link>
        </div>
      </div>
    </footer>      
  </div>
  );
};

export default TrainingEnrollmentForm;
 